<template>
  <!-- Полный итог запуска сервиса epgimport и легенда полей: открывается
       по щелчку на строке «Последний запуск …» в списке каналов. -->
  <div class="small">
    <table class="table table-sm mb-3">
      <tbody>
        <tr>
          <th class="w-25">{{ t("вид") }}</th>
          <td>{{ title }}</td>
        </tr>
        <tr>
          <th>{{ t("источник_запуска") }}</th>
          <td>{{ origin }}</td>
        </tr>
        <tr>
          <th>{{ t("начало") }}</th>
          <td>{{ time(run.started_at) }}</td>
        </tr>
        <tr>
          <th>{{ t("окончание") }}</th>
          <td>{{ time(run.finished_at) }}</td>
        </tr>
        <tr>
          <th>{{ t("статус") }}</th>
          <td>{{ run.status }}</td>
        </tr>
        <tr v-if="run.error">
          <th>{{ t("ошибка") }}</th>
          <td>{{ run.error }}</td>
        </tr>
        <tr>
          <th>{{ t("запуск") }}</th>
          <td class="text-muted">{{ run.run_id }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Каталог каналов: итог по источникам. -->
    <template v-if="catalog">
      <h6>{{ t("источники") }}</h6>
      <table class="table table-sm mb-3">
        <thead>
          <tr>
            <th>{{ t("источник") }}</th>
            <th>{{ t("статус") }}</th>
            <th class="text-right">new</th>
            <th class="text-right">disabled</th>
            <th class="text-right">reenabled</th>
            <th class="text-right">unchanged</th>
            <th>{{ t("сообщение") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in catalogRows" :key="c.source_id">
            <td>{{ c.source_id }} {{ c.source_name }}</td>
            <td>{{ c.status }}</td>
            <td class="text-right">{{ c.new || 0 }}</td>
            <td class="text-right">{{ c.disabled || 0 }}</td>
            <td class="text-right">{{ c.reenabled || 0 }}</td>
            <td class="text-right">{{ c.unchanged || 0 }}</td>
            <td>{{ c.message }}</td>
          </tr>
          <tr class="font-weight-bold">
            <td colspan="2">{{ $t("message.Импорт_EPG_сервис_итого") }}</td>
            <td class="text-right">{{ catalogTotals.new || 0 }}</td>
            <td class="text-right">{{ catalogTotals.disabled || 0 }}</td>
            <td class="text-right">{{ catalogTotals.reenabled || 0 }}</td>
            <td class="text-right">{{ catalogTotals.unchanged || 0 }}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- Импорт передач: общие числа, источники, каналы не ok. -->
    <template v-else>
      <table class="table table-sm mb-3">
        <tbody>
          <tr>
            <th class="w-25">{{ t("каналов") }}</th>
            <td>{{ totals.channels || 0 }}</td>
          </tr>
          <tr>
            <th>{{ t("по_статусам") }}</th>
            <td>{{ byStatus }}</td>
          </tr>
          <tr v-for="k in counters" :key="k">
            <th>{{ k }}</th>
            <td>{{ totals[k] || 0 }}</td>
          </tr>
        </tbody>
      </table>

      <h6 v-if="sources.length">{{ t("источники") }}</h6>
      <table v-if="sources.length" class="table table-sm mb-3">
        <tbody>
          <tr v-for="s in sources" :key="s.source_id">
            <td>{{ s.source_id }} {{ s.name }}</td>
            <td>{{ s.status }}</td>
            <td>{{ s.message }}</td>
          </tr>
        </tbody>
      </table>

      <h6>{{ t("каналы_не_ok") }}</h6>
      <p v-if="!badChannels.length" class="text-muted">{{ t("все_ok") }}</p>
      <table v-else class="table table-sm mb-3">
        <thead>
          <tr>
            <th>{{ t("канал") }}</th>
            <th>{{ t("источник") }}</th>
            <th>{{ t("статус") }}</th>
            <th>{{ t("сообщение") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in badChannels" :key="c.channel_id">
            <td>{{ c.channel_id }} {{ c.channel_name }}</td>
            <td>{{ c.source_id }}</td>
            <td>{{ c.status }}</td>
            <td>{{ c.message }}</td>
          </tr>
        </tbody>
      </table>
    </template>

    <h6>{{ t("легенда") }}</h6>
    <dl class="mb-0">
      <dt>{{ t("статус") }}</dt>
      <dd>
        <div v-for="s in runStatuses" :key="s"><code>{{ s }}</code> — {{ t("run_" + s) }}</div>
      </dd>
      <template v-if="catalog">
        <dt>{{ t("источник") }}: {{ t("статус") }}</dt>
        <dd>
          <div v-for="s in catalogStatuses" :key="s"><code>{{ s }}</code> — {{ t("cat_" + s) }}</div>
        </dd>
        <dt>{{ t("источники") }}: new, disabled, reenabled, unchanged</dt>
        <dd>
          <div v-for="k in catalogCounters" :key="k"><code>{{ k }}</code> — {{ t("cat_" + k) }}</div>
        </dd>
      </template>
      <template v-else>
        <dt>{{ t("канал") }}: {{ t("статус") }}</dt>
        <dd>
          <div v-for="s in channelStatuses" :key="s"><code>{{ s }}</code> — {{ t("st_" + s) }}</div>
        </dd>
        <dt>{{ t("каналов") }}: inserted, deleted, …</dt>
        <dd>
          <div v-for="k in counters" :key="k"><code>{{ k }}</code> — {{ t("n_" + k) }}</div>
        </dd>
      </template>
    </dl>
  </div>
</template>

<script>
import { dateHhmm, isCatalog, runTitle } from "./epgImportService";

const RUN_STATUSES = ["succeeded", "partial", "failed", "cancelled"];
const CHANNEL_STATUSES = [
  "ok",
  "no_future_programmes",
  "no_programmes",
  "source_unavailable",
  "binding_changed",
  "not_bound",
  "unsupported_handler",
  "locked_by_other_writer",
  "commit_unknown",
  "error"
];
const COUNTERS = [
  "inserted",
  "deleted",
  "tail_kept",
  "kept_before_from",
  "past_deleted",
  "past_inserted",
  "past_skipped",
  "dropped_after_shift"
];
const CATALOG_STATUSES = ["ok", "empty_catalog", "source_unavailable", "source_changed", "unsupported_handler"];
const CATALOG_COUNTERS = ["new", "disabled", "reenabled", "unchanged"];

export default {
  name: "EpgRunDetails",
  props: {
    run: { type: Object, required: true }
  },
  data() {
    return {
      runStatuses: RUN_STATUSES,
      channelStatuses: CHANNEL_STATUSES,
      counters: COUNTERS,
      catalogStatuses: CATALOG_STATUSES,
      catalogCounters: CATALOG_COUNTERS
    };
  },
  computed: {
    summary() {
      return this.run.summary || {};
    },
    catalog() {
      return isCatalog(this.run);
    },
    title() {
      return runTitle(this.run, key => this.$i18n.t("message." + key));
    },
    origin() {
      const o = this.run.origin;
      return o ? this.t("origin_" + o) : "";
    },
    totals() {
      return this.summary.totals || {};
    },
    byStatus() {
      const m = this.totals.by_status || {};
      return Object.keys(m)
        .sort()
        .map(k => k + " " + m[k])
        .join(", ");
    },
    sources() {
      return this.summary.sources || [];
    },
    badChannels() {
      return (this.summary.channels || []).filter(c => c.status !== "ok");
    },
    catalogRows() {
      return this.summary.catalog || [];
    },
    catalogTotals() {
      return this.summary.catalog_totals || {};
    }
  },
  methods: {
    t(key) {
      return this.$i18n.t("message.Итог_EPG." + key);
    },
    time(iso) {
      return dateHhmm(iso);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/modules/EpgRunDetails.vue